import {noop} from 'lodash';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {KeyboardEventHandler} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Input, InputTypesEnum} from '../../../../libs/shared/ui/src/components/inputs/input';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FloatingBar} from './floatingBar';

/*
 * Props.
 */

interface FloatingTextInputProps {
  className?: string;
  placeholder?: string;
  shouldFocus?: boolean;
  initialValue?: string;
  onValueSelect?: (arg: string) => void;
  onRequestClose?: () => void;
}

/*
 * Style.
 */

const StyledFloatingBar = styled(FloatingBar)`
  background-color: ${(p) => p.theme.foreground};

  display: flex;
`;

const StyledInput = styled(Input)`
  line-height: 18px;
  padding: 10px 10px 10px 13px;
  width: 273px;
  font-weight: inherit;

  ::placeholder {
    font-weight: normal;
  }
`;

const StyledClearButtonWrapper = styled.div`
  margin-left: -12px;
`;

const StyledClearButton = styled(IconButton)`
  margin: 6px;
`;

/*
 * Component.
 */

export const FloatingTextInput: FC<FloatingTextInputProps> = (props) => {
  const theme = useUserTheme();
  const {initialValue, onValueSelect = noop, onRequestClose = noop} = props;
  const [value, setValue] = useState(initialValue || '');

  // Helps a prevent recursion in event handlers that indirectly cause a blur event.
  const isHandlingEventRef = useRef(false);
  const handleEventExclusively = (callback: () => void) => {
    if (isHandlingEventRef.current) {
      return;
    }

    isHandlingEventRef.current = true;
    try {
      callback();
    } finally {
      isHandlingEventRef.current = false;
    }
  };

  const onInputKeyDown: KeyboardEventHandler = (event) => {
    switch (event.key) {
      case Key.Enter:
        event.preventDefault();
        handleEventExclusively(() => onValueSelect(value));
        break;

      case Key.Escape:
        event.preventDefault();
        handleEventExclusively(() => onRequestClose());
        break;

      // Do nothing.
      default:
    }
  };

  const onInputBlur: () => void = () => {
    handleEventExclusively(() => onValueSelect(value));
  };

  const onClearButtonClick: () => void = () => {
    handleEventExclusively(() => onValueSelect(''));
    // Clear the input field in case the event handler did not remove us from screen.
    setValue('');
  };

  return (
    <StyledFloatingBar className={props.className}>
      <StyledInput
        type={InputTypesEnum.TEXT}
        placeholder={props.placeholder}
        value={value}
        onKeyDown={onInputKeyDown}
        onChange={setValue}
        shouldFocus={props.shouldFocus}
        onBlur={onInputBlur}
      />
      <StyledClearButtonWrapper>
        <StyledClearButton
          iconName="clearSmall"
          label={<Trans id="floatingbar-clearbutton-label">Clear</Trans>}
          backgroundHoverColor={theme.alphas.transparent}
          shouldPreventMouseDown
          onClick={onClearButtonClick}
        />
      </StyledClearButtonWrapper>
    </StyledFloatingBar>
  );
};
